import React, { useState, useEffect, useMemo } from 'react';
import {
  PortvilleBillingSettings,
  BillingSettingsFormData
} from '../../../../types/portvilleBilling';
import { PortvilleRoomWithResidency, FacilityFormData } from '../../../../types/portvilleRoom';

interface BillingSettingsFormProps {
  settings?: PortvilleBillingSettings;
  rooms: PortvilleRoomWithResidency[];
  selectedRoomId: string;
  onSubmit: (formData: BillingSettingsFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  onCreateFacility?: (facilityData: FacilityFormData) => Promise<string>; // 시설물 생성 함수
}

const BillingSettingsForm: React.FC<BillingSettingsFormProps> = ({
  settings,
  rooms,
  selectedRoomId,
  onSubmit,
  onCancel,
  isSubmitting,
  onCreateFacility
}) => {
  // 폼 데이터 상태
  const [formData, setFormData] = useState<BillingSettingsFormData>({
    roomId: selectedRoomId || '',
    roomNumber: '',
    roomType: '호실',

    // 연락처 정보
    contractorPhoneNumber: '',
    roommatePhoneNumber: '',

    // 기본 요금
    monthlyRent: 0,
    managementFee: 0,
    managementFeeVATRate: 10,

    // 기타 요금
    communicationFee: 0,
    communicationFeeVATRate: 10,

    // 호실별 설정
    electricityAreaContributionRatio: 0.0040329,
    waterAreaContributionRatio: 0.005085,
    roomTypeDetail: '포트빌' as '포트빌' | '늘해랑',

    // 청구 설정
    issueDay: 1,
    dueDay: 25,
    autoGenerate: true,

    // 적용 기간
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: undefined
  });

  // 시설물 생성 모드 상태
  const [isCreatingFacility, setIsCreatingFacility] = useState(false);
  const [facilityFormData, setFacilityFormData] = useState<FacilityFormData>({
    facilityCode: '',
    facilityName: '',
    facilityType: '전기차 충전소',
    responsiblePerson: {
      name: '',
      phoneNumber: '',
      organization: ''
    }
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set());

  // 기존 설정이 있는 경우 폼 데이터 초기화
  useEffect(() => {
    if (settings) {
      const room = rooms.find(r => r.id === settings.roomId);

      // 실제 room의 roomType을 우선으로 사용, 없으면 settings의 roomType, 그것도 없으면 '호실'
      const actualRoomType = room?.roomType || settings.roomType || '호실';
      const actualRoomTypeDetail = room?.roomType === '시설물' ?
        (room?.facilityInfo?.facilityType || '전기차 충전소') :
        (settings.roomSettings?.roomType ?? '포트빌');

      setFormData({
        roomId: settings.roomId,
        roomNumber: settings.roomNumber,
        roomType: actualRoomType,

        // 연락처 정보
        contractorPhoneNumber: settings.contactInfo?.contractorPhoneNumber || '',
        roommatePhoneNumber: settings.contactInfo?.roommatePhoneNumber || '',

        monthlyRent: settings.baseRates.monthlyRent,
        managementFee: settings.baseRates.managementFee,
        managementFeeVATRate: settings.baseRates.managementFeeVATRate,

        communicationFee: settings.baseRates.communicationFee,
        communicationFeeVATRate: settings.baseRates.communicationFeeVATRate,

        electricityAreaContributionRatio: settings.roomSettings?.electricityAreaContributionRatio ?? (settings.roomSettings as any)?.areaContributionRatio ?? 0.0040329,
        waterAreaContributionRatio: settings.roomSettings?.waterAreaContributionRatio ?? (settings.roomSettings as any)?.areaContributionRatio ?? 0.005085,
        roomTypeDetail: actualRoomTypeDetail,

        issueDay: settings.billingConfig.issueDay,
        dueDay: settings.billingConfig.dueDay,
        autoGenerate: settings.billingConfig.autoGenerate,

        effectiveFrom: settings.effectiveFrom.toISOString().split('T')[0],
        effectiveTo: settings.effectiveTo?.toISOString().split('T')[0]
      });
    }
  }, [settings, rooms]);


  // 선택된 호실 정보
  const selectedRoom = useMemo(() => {
    return rooms.find(room => room.id === formData.roomId);
  }, [rooms, formData.roomId]);

  // 폼 데이터 업데이트
  const updateFormData = (field: keyof BillingSettingsFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // 에러 클리어
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // 호실 선택 시 호실번호 자동 설정
  const handleRoomChange = (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);

    let roomTypeDetail: '포트빌' | '늘해랑' | '전기차 충전소' | '통신 중계기' | '기타';
    if (room?.roomType === '시설물') {
      roomTypeDetail = (room?.facilityInfo?.facilityType || '전기차 충전소') as '전기차 충전소' | '통신 중계기' | '기타';
    } else {
      roomTypeDetail = '포트빌';
    }

    // 현재 시스템의 계약 정보로부터 연락처 기본값 설정
    let contractorPhoneNumber = '';
    let roommatePhoneNumber = '';

    if (room?.roomType === '호실' && room?.currentResident) {
      // 호실의 경우 현재 입주자 정보를 기본값으로 설정
      contractorPhoneNumber = room.currentResident.phoneNumber || '';
      roommatePhoneNumber = room.roommateContact || '';
    }

    setFormData(prev => ({
      ...prev,
      roomId,
      roomNumber: room?.roomNumber || '',
      roomType: room?.roomType || '호실',
      roomTypeDetail,
      contractorPhoneNumber,
      roommatePhoneNumber
    }));
  };

  // 시설물 생성 모드 토글
  const handleToggleFacilityMode = () => {
    setIsCreatingFacility(!isCreatingFacility);
    if (!isCreatingFacility) {
      // 시설물 생성 모드로 전환 시 폼 초기화
      setFacilityFormData({
        facilityCode: '',
        facilityName: '',
        facilityType: '전기차 충전소',
        responsiblePerson: {
          name: '',
          phoneNumber: '',
          organization: ''
        }
      });
    }
  };

  // 시설물 생성
  const handleCreateFacility = async () => {
    if (!onCreateFacility) return;
    
    try {
      const roomId = await onCreateFacility(facilityFormData);
      if (roomId) {
        setFormData(prev => ({
          ...prev,
          roomId,
          roomNumber: facilityFormData.facilityCode,
          roomType: '시설물',
          roomTypeDetail: facilityFormData.facilityType
        }));
        setIsCreatingFacility(false);
      }
    } catch (error) {
      console.error('시설물 생성 실패:', error);
    }
  };

  // 유효성 검사
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // 필수 필드 검사
    if (!formData.roomId) newErrors.roomId = '호실을 선택해 주세요.';
    if (!formData.contractorPhoneNumber?.trim()) newErrors.contractorPhoneNumber = '계약자 연락처를 입력해 주세요.';
    if (formData.monthlyRent !== undefined && formData.monthlyRent < 0) newErrors.monthlyRent = '월 임대료는 0 이상이어야 합니다.';
    if (formData.managementFee !== undefined && formData.managementFee < 0) newErrors.managementFee = '관리비는 0 이상이어야 합니다.';
    
    // 부가세율 검사
    if (formData.managementFeeVATRate !== undefined && (formData.managementFeeVATRate < 0 || formData.managementFeeVATRate > 100)) {
      newErrors.managementFeeVATRate = '관리비 부가세율은 0~100% 사이여야 합니다.';
    }
    
    // 면적기여비율 검사
    if (formData.electricityAreaContributionRatio !== undefined && (formData.electricityAreaContributionRatio < 0 || formData.electricityAreaContributionRatio > 1)) {
      newErrors.electricityAreaContributionRatio = '전기 면적기여비율은 0~1 사이여야 합니다.';
    }
    if (formData.waterAreaContributionRatio !== undefined && (formData.waterAreaContributionRatio < 0 || formData.waterAreaContributionRatio > 1)) {
      newErrors.waterAreaContributionRatio = '수도 면적기여비율은 0~1 사이여야 합니다.';
    }
    
    // 호실/시설물 타입 검사
    if (formData.roomType && !['호실', '시설물'].includes(formData.roomType)) {
      newErrors.roomType = '타입은 호실 또는 시설물이어야 합니다.';
    }
    
    // 상세 타입 검사
    if (formData.roomType === '호실' && formData.roomTypeDetail && !['포트빌', '늘해랑'].includes(formData.roomTypeDetail)) {
      newErrors.roomTypeDetail = '호실타입은 포트빌 또는 늘해랑이어야 합니다.';
    }
    if (formData.roomType === '시설물' && formData.roomTypeDetail && !['전기차 충전소', '통신 중계기', '기타'].includes(formData.roomTypeDetail)) {
      newErrors.roomTypeDetail = '시설물 타입은 전기차 충전소, 통신 중계기, 기타 중 하나여야 합니다.';
    }
    
    // 기타 요금 검사
    if (formData.communicationFee !== undefined && formData.communicationFee < 0) {
      newErrors.communicationFee = '통신요금은 0 이상이어야 합니다.';
    }
    if (formData.communicationFeeVATRate !== undefined && (formData.communicationFeeVATRate < 0 || formData.communicationFeeVATRate > 100)) {
      newErrors.communicationFeeVATRate = '통신요금 부가세율은 0~100% 사이여야 합니다.';
    }

    // 청구 설정 검사
    if (formData.issueDay < 1 || formData.issueDay > 28) {
      newErrors.issueDay = '발행일은 1일~28일 사이여야 합니다.';
    }
    if (formData.dueDay < 1 || formData.dueDay > 31) {
      newErrors.dueDay = '납부 기한은 1일~31일 사이여야 합니다.';
    }

    // 적용 기간 검사
    if (!formData.effectiveFrom) newErrors.effectiveFrom = '적용 시작일을 입력해 주세요.';
    if (formData.effectiveTo && formData.effectiveFrom && new Date(formData.effectiveTo) <= new Date(formData.effectiveFrom)) {
      newErrors.effectiveTo = '적용 종료일은 시작일보다 나중이어야 합니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('폼 제출 실패:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* 호실/시설물 선택 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {isCreatingFacility ? '시설물 생성' : '호실 선택'} *
            </label>
            {!settings && (
              <button
                type="button"
                onClick={handleToggleFacilityMode}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {isCreatingFacility ? '호실 선택으로 돌아가기' : '시설물 생성'}
              </button>
            )}
          </div>
          
          {isCreatingFacility ? (
            <div className="space-y-4">
              {/* 시설물 코드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시설물 코드 *
                </label>
                <input
                  type="text"
                  placeholder="예: EV-001, COMMON-001"
                  value={facilityFormData.facilityCode}
                  onChange={(e) => setFacilityFormData(prev => ({
                    ...prev,
                    facilityCode: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* 시설물명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시설물명 *
                </label>
                <input
                  type="text"
                  placeholder="예: 전기차 충전소 1호기"
                  value={facilityFormData.facilityName}
                  onChange={(e) => setFacilityFormData(prev => ({
                    ...prev,
                    facilityName: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* 시설물 타입 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  시설물 타입 *
                </label>
                <select
                  value={facilityFormData.facilityType}
                  onChange={(e) => setFacilityFormData(prev => ({
                    ...prev,
                    facilityType: e.target.value as '전기차 충전소' | '통신 중계기' | '기타'
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="전기차 충전소">전기차 충전소</option>
                  <option value="통신 중계기">통신 중계기</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              
              {/* 담당자 정보 */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">담당자 정보</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    담당자명 *
                  </label>
                  <input
                    type="text"
                    placeholder="담당자 성명"
                    value={facilityFormData.responsiblePerson.name}
                    onChange={(e) => setFacilityFormData(prev => ({
                      ...prev,
                      responsiblePerson: {
                        ...prev.responsiblePerson,
                        name: e.target.value
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    연락처 *
                  </label>
                  <input
                    type="text"
                    placeholder="010-1234-5678"
                    value={facilityFormData.responsiblePerson.phoneNumber}
                    onChange={(e) => setFacilityFormData(prev => ({
                      ...prev,
                      responsiblePerson: {
                        ...prev.responsiblePerson,
                        phoneNumber: e.target.value
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    소속 *
                  </label>
                  <input
                    type="text"
                    placeholder="소속 기관명"
                    value={facilityFormData.responsiblePerson.organization}
                    onChange={(e) => setFacilityFormData(prev => ({
                      ...prev,
                      responsiblePerson: {
                        ...prev.responsiblePerson,
                        organization: e.target.value
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              {/* 시설물 생성 버튼 */}
              <button
                type="button"
                onClick={handleCreateFacility}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                disabled={!facilityFormData.facilityCode || !facilityFormData.facilityName || !facilityFormData.responsiblePerson.name}
              >
                시설물 생성
              </button>
            </div>
          ) : (
            <select
              value={formData.roomId}
              onChange={(e) => handleRoomChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.roomId ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={!!settings} // 수정 시에는 호실 변경 불가
            >
              <option value="">호실/시설물을 선택하세요</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>
                  {room.roomNumber}호 
                  {room.roomType === '시설물' ? 
                    (room.facilityInfo ? ` - ${room.facilityInfo.facilityName} (${room.facilityInfo.facilityType})` : ' - 시설물') :
                    (room.currentResident && ` - ${room.currentResident.name} (${room.currentResident.organization})`)
                  }
                </option>
              ))}
            </select>
          )}
          {errors.roomId && <p className="mt-1 text-sm text-red-600">{errors.roomId}</p>}
        </div>

        {selectedRoom && (
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium text-gray-900 mb-2">
              선택된 {selectedRoom.roomType === '시설물' ? '시설물' : '호실'} 정보
            </h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">{selectedRoom.roomType === '시설물' ? '시설물 코드' : '호실'}:</span> {selectedRoom.roomNumber}</p>
              
              {selectedRoom.roomType === '시설물' && selectedRoom.facilityInfo ? (
                <>
                  <p><span className="font-medium">시설물명:</span> {selectedRoom.facilityInfo.facilityName}</p>
                  <p><span className="font-medium">시설물 타입:</span> {selectedRoom.facilityInfo.facilityType}</p>
                  <p><span className="font-medium">담당자:</span> {selectedRoom.facilityInfo.responsiblePerson.name}</p>
                  <p><span className="font-medium">연락처:</span> {selectedRoom.facilityInfo.responsiblePerson.phoneNumber}</p>
                  <p><span className="font-medium">소속:</span> {selectedRoom.facilityInfo.responsiblePerson.organization}</p>
                </>
              ) : selectedRoom.currentResident ? (
                <>
                  <p><span className="font-medium">입주자:</span> {selectedRoom.currentResident.name}</p>
                  <p><span className="font-medium">소속:</span> {selectedRoom.currentResident.organization}</p>
                </>
              ) : null}
              
              <p><span className="font-medium">상태:</span> {selectedRoom.status}</p>
            </div>
          </div>
        )}
      </div>

      {/* 연락처 정보 */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">연락처 정보</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              계약자 연락처 *
            </label>
            <input
              type="tel"
              value={formData.contractorPhoneNumber || ''}
              onChange={(e) => updateFormData('contractorPhoneNumber', e.target.value)}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'contractorPhoneNumber']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('contractorPhoneNumber');
                return newSet;
              })}
              placeholder={focusedFields.has('contractorPhoneNumber') ? '' : '010-1234-5678'}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.contractorPhoneNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              inputMode="tel"
            />
            {errors.contractorPhoneNumber && <p className="mt-1 text-sm text-red-600">{errors.contractorPhoneNumber}</p>}
            <p className="mt-1 text-xs text-gray-500">
              {selectedRoom?.currentResident?.phoneNumber
                ? `현재 계약자 연락처: ${selectedRoom.currentResident.phoneNumber}`
                : '계약자의 연락처를 입력하세요'
              }
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              동거인 연락처 (선택사항)
            </label>
            <input
              type="tel"
              value={formData.roommatePhoneNumber || ''}
              onChange={(e) => updateFormData('roommatePhoneNumber', e.target.value)}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'roommatePhoneNumber']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('roommatePhoneNumber');
                return newSet;
              })}
              placeholder={focusedFields.has('roommatePhoneNumber') ? '' : '010-9876-5432'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              inputMode="tel"
            />
            <p className="mt-1 text-xs text-gray-500">
              {selectedRoom?.roommateContact
                ? `현재 동거인 연락처: ${selectedRoom.roommateContact}`
                : '동거인이 있는 경우 연락처를 입력하세요'
              }
            </p>
          </div>
        </div>
      </div>

      {/* 기본 요금 설정 */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">기본 요금 설정</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              월 임대료 (원) *
            </label>
            <input
              type="number"
              value={formData.monthlyRent !== undefined ? formData.monthlyRent.toString() : ''}
              onChange={(e) => {
                const value = e.target.value;
                updateFormData('monthlyRent', value === '' ? undefined : (parseInt(value) || 0));
              }}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'monthlyRent']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('monthlyRent');
                return newSet;
              })}
              placeholder={focusedFields.has('monthlyRent') ? '' : '0'}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                errors.monthlyRent ? 'border-red-500' : 'border-gray-300'
              }`}
              inputMode="numeric"
              step="1000"
            />
            {errors.monthlyRent && <p className="mt-1 text-sm text-red-600">{errors.monthlyRent}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              관리비 (원) *
            </label>
            <input
              type="number"
              value={formData.managementFee !== undefined ? formData.managementFee.toString() : ''}
              onChange={(e) => {
                const value = e.target.value;
                updateFormData('managementFee', value === '' ? undefined : (parseInt(value) || 0));
              }}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'managementFee']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('managementFee');
                return newSet;
              })}
              placeholder={focusedFields.has('managementFee') ? '' : '0'}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                errors.managementFee ? 'border-red-500' : 'border-gray-300'
              }`}
              inputMode="numeric"
              step="1000"
            />
            {errors.managementFee && <p className="mt-1 text-sm text-red-600">{errors.managementFee}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              관리비 부가세율 (%)
            </label>
            <input
              type="number"
              value={formData.managementFeeVATRate !== undefined ? formData.managementFeeVATRate.toString() : ''}
              onChange={(e) => {
                const value = e.target.value;
                updateFormData('managementFeeVATRate', value === '' ? undefined : (parseFloat(value) || 0));
              }}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'managementFeeVATRate']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('managementFeeVATRate');
                return newSet;
              })}
              placeholder={focusedFields.has('managementFeeVATRate') ? '' : '0'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              inputMode="decimal"
              max="100"
              step="0.1"
            />
          </div>
        </div>
      </div>

      {/* 호실별 설정 */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">호실별 설정</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                전기 면적기여비율 *
              </label>
              <input
                type="number"
                value={formData.electricityAreaContributionRatio !== undefined ? formData.electricityAreaContributionRatio.toString() : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFormData('electricityAreaContributionRatio', value === '' ? undefined : (isNaN(parseFloat(value)) ? undefined : parseFloat(value)));
                }}
                onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'electricityAreaContributionRatio']))}
                onBlur={() => setFocusedFields(prev => {
                  const newSet = new Set(prev);
                  newSet.delete('electricityAreaContributionRatio');
                  return newSet;
                })}
                placeholder={focusedFields.has('electricityAreaContributionRatio') ? '' : '0.0040329'}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  errors.electricityAreaContributionRatio ? 'border-red-500' : 'border-gray-300'
                }`}
                inputMode="decimal"
                step="0.0000001"
                min="0"
                max="1"
              />
              {errors.electricityAreaContributionRatio && <p className="mt-1 text-sm text-red-600">{errors.electricityAreaContributionRatio}</p>}
              <p className="mt-1 text-xs text-gray-500">전기 요금 분할 시 사용되는 비율 (기본값: 0.0040329)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                수도 면적기여비율 *
              </label>
              <input
                type="number"
                value={formData.waterAreaContributionRatio !== undefined ? formData.waterAreaContributionRatio.toString() : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFormData('waterAreaContributionRatio', value === '' ? undefined : (isNaN(parseFloat(value)) ? undefined : parseFloat(value)));
                }}
                onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'waterAreaContributionRatio']))}
                onBlur={() => setFocusedFields(prev => {
                  const newSet = new Set(prev);
                  newSet.delete('waterAreaContributionRatio');
                  return newSet;
                })}
                placeholder={focusedFields.has('waterAreaContributionRatio') ? '' : '0.005085'}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  errors.waterAreaContributionRatio ? 'border-red-500' : 'border-gray-300'
                }`}
                inputMode="decimal"
                step="0.0000001"
                min="0"
                max="1"
              />
              {errors.waterAreaContributionRatio && <p className="mt-1 text-sm text-red-600">{errors.waterAreaContributionRatio}</p>}
                <p className="mt-1 text-xs text-gray-500">수도 요금 분할 시 사용되는 비율 (기본값: 0.005085)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.roomType === '시설물' ? '시설물 타입' : '호실타입'} *
              </label>
              {formData.roomType === '시설물' ? (
                <select
                  value={formData.roomTypeDetail || '전기차 충전소'}
                  onChange={(e) => updateFormData('roomTypeDetail', e.target.value as '전기차 충전소' | '통신 중계기' | '기타')}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    errors.roomTypeDetail ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="전기차 충전소">전기차 충전소</option>
                  <option value="통신 중계기">통신 중계기</option>
                  <option value="기타">기타</option>
                </select>
              ) : (
                <select
                  value={formData.roomTypeDetail || '포트빌'}
                  onChange={(e) => updateFormData('roomTypeDetail', e.target.value as '포트빌' | '늘해랑')}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    errors.roomTypeDetail ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="포트빌">포트빌</option>
                  <option value="늘해랑">늘해랑</option>
                </select>
              )}
              {errors.roomTypeDetail && <p className="mt-1 text-sm text-red-600">{errors.roomTypeDetail}</p>}
              <p className="mt-1 text-xs text-gray-500">
                {formData.roomType === '시설물' ? '시설물의 타입을 선택하세요' : '호실의 타입을 선택하세요'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 기타 요금 설정 */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">기타 요금 설정</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              통신요금 (원)
            </label>
            <input
              type="number"
              value={formData.communicationFee !== undefined ? formData.communicationFee.toString() : ''}
              onChange={(e) => {
                const value = e.target.value;
                updateFormData('communicationFee', value === '' ? undefined : (parseInt(value) || 0));
              }}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'communicationFee']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('communicationFee');
                return newSet;
              })}
              placeholder={focusedFields.has('communicationFee') ? '' : '0'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              inputMode="numeric"
              step="100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              통신요금 부가세율 (%)
            </label>
            <input
              type="number"
              value={formData.communicationFeeVATRate !== undefined ? formData.communicationFeeVATRate.toString() : ''}
              onChange={(e) => {
                const value = e.target.value;
                updateFormData('communicationFeeVATRate', value === '' ? undefined : (parseFloat(value) || 0));
              }}
              onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'communicationFeeVATRate']))}
              onBlur={() => setFocusedFields(prev => {
                const newSet = new Set(prev);
                newSet.delete('communicationFeeVATRate');
                return newSet;
              })}
              placeholder={focusedFields.has('communicationFeeVATRate') ? '' : '0'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              inputMode="decimal"
              max="100"
              step="0.1"
            />
          </div>

        </div>
      </div>

      {/* 청구 설정 */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">청구 설정</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              매월 발행일 *
            </label>
            <select
              value={formData.issueDay}
              onChange={(e) => updateFormData('issueDay', parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.issueDay ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>{day}일</option>
              ))}
            </select>
            {errors.issueDay && <p className="mt-1 text-sm text-red-600">{errors.issueDay}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              납부 기한일 *
            </label>
            <select
              value={formData.dueDay}
              onChange={(e) => updateFormData('dueDay', parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.dueDay ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day}>{day}일</option>
              ))}
            </select>
            {errors.dueDay && <p className="mt-1 text-sm text-red-600">{errors.dueDay}</p>}
          </div>

          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.autoGenerate}
                onChange={(e) => updateFormData('autoGenerate', e.target.checked)}
                className="mr-2"
              />
              자동 청구서 생성
            </label>
          </div>
        </div>
      </div>

      {/* 적용 기간 */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">적용 기간</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              적용 시작일 *
            </label>
            <input
              type="date"
              value={formData.effectiveFrom}
              onChange={(e) => updateFormData('effectiveFrom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.effectiveFrom ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.effectiveFrom && <p className="mt-1 text-sm text-red-600">{errors.effectiveFrom}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              적용 종료일 (선택사항)
            </label>
            <input
              type="date"
              value={formData.effectiveTo || ''}
              onChange={(e) => updateFormData('effectiveTo', e.target.value || undefined)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.effectiveTo ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.effectiveTo && <p className="mt-1 text-sm text-red-600">{errors.effectiveTo}</p>}
            <p className="mt-1 text-sm text-gray-500">설정하지 않으면 무기한 적용됩니다.</p>
          </div>
        </div>
      </div>

      {/* 버튼 */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          disabled={isSubmitting}
        >
          취소
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? '저장 중...' : (settings ? '수정' : '저장')}
        </button>
      </div>
    </form>
  );
};

export default BillingSettingsForm;
